import { clashKey, type ClashResult } from '@viewer/clash/lib'
import type { ClashGuidMap, ClashTopicDraft } from './clashBcf'
import type { Especialidade } from './clashEspecialidades'
import { textoDaCondicao, type EspecialidadeDe, type RegraDeExclusao } from './clashExclusao'
import {
  ESPECIALIDADES,
  PRIORITIES,
  PRIORITY_BCF,
  TOPIC_TYPE_CLASH,
  triageOf,
  type ClashPriority,
  type ClashTriageMap,
} from './clashTriage'

// ---------------------------------------------------------------------------
// "Corrigir na origem": o quarto passo do funil (docs/CLASH.md,
// seccao 11).
//
// Uma duplicacao de modelacao EST/ARQ nao se resolve conflito a conflito:
// o ARQ muda a convencao (modela so as camadas nao estruturais) e os 183
// conflitos desaparecem na revisao seguinte. Para essas regras, em vez de
// N topicos, UM topico para o modelador: a regra, o motivo, a condicao e
// a lista dos elementos (GlobalId e nome), por especialidade.
//
// O topico tem identidade propria ("regra:<id>"): com o identificador do
// projecto, sai sempre com o mesmo Guid (voltar a criar actualiza-o
// noutra plataforma, nao o duplica).
//
// Puro: quem chama resolve os GlobalIds e os nomes e escreve o BCF
// (clashBcfExport.ts, exportarTopicoDoModelador).
// ---------------------------------------------------------------------------

/** Elementos listados no topico, no maximo (a descricao de um BCF le-se
 *  num ecra; o resto vai contado). */
export const MAXIMO_DE_ELEMENTOS_NO_TOPICO = 400

export interface ElementoDoModelador {
  modelId: string
  localId: number
  especialidade: Especialidade | null
  classe: string | null
  guid: string | null
  nome: string | null
}

/** Os elementos distintos dos conflitos, pela ordem de aparecimento. */
export function elementosDosConflitos(
  results: readonly ClashResult[],
  o: {
    guids: ClashGuidMap
    nomes?: Record<string, [string | null, string | null]>
    especialidadeDe: EspecialidadeDe
  },
): ElementoDoModelador[] {
  const vistos = new Map<string, ElementoDoModelador>()
  for (const r of results) {
    const k = clashKey(r)
    const g = o.guids[k] ?? [null, null]
    const n = o.nomes?.[k] ?? [null, null]
    const lados: [string, number, string | null | undefined, string | null, string | null][] = [
      [r.pair.modelA, r.pair.localIdA, r.categoryA, g[0], n[0]],
      [r.pair.modelB, r.pair.localIdB, r.categoryB, g[1], n[1]],
    ]
    for (const [modelId, localId, classe, guid, nome] of lados) {
      const id = `${modelId}:${localId}`
      if (vistos.has(id)) continue
      vistos.set(id, {
        modelId,
        localId,
        especialidade: o.especialidadeDe(modelId),
        classe: classe ?? null,
        guid,
        nome,
      })
    }
  }
  return [...vistos.values()]
}

function contagem(n: number, um: string, varios: string): string {
  return `${n} ${n === 1 ? um : varios}`
}

/** O rascunho do topico para o modelador. */
export function rascunhoDoModelador(
  regra: RegraDeExclusao,
  results: readonly ClashResult[],
  o: {
    guids: ClashGuidMap
    nomes?: Record<string, [string | null, string | null]>
    triage: ClashTriageMap
    especialidadeDe: EspecialidadeDe
  },
): ClashTopicDraft {
  const keys = results.map(clashKey)
  const entradas = keys.map((k) => triageOf(o.triage, k))
  const elementos = elementosDosConflitos(results, o)
  // O responsavel: o mais frequente dos conflitos (numa duplicacao EST/ARQ,
  // o ARQ); a quem comunicar: os outros, sem repetidos.
  const vezes = new Map<string, number>()
  for (const e of entradas) {
    const a = (e.assignedTo ?? '').trim()
    if (a) vezes.set(a, (vezes.get(a) ?? 0) + 1)
  }
  const responsavel = [...vezes].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? null
  const notify = [...new Set(entradas.flatMap((e) => e.notify ?? []))].filter((x) => x !== responsavel)
  let prioridade: ClashPriority | null = null
  for (const e of entradas) {
    if (e.priority && (prioridade === null || PRIORITIES.indexOf(e.priority) < PRIORITIES.indexOf(prioridade))) {
      prioridade = e.priority
    }
  }
  const recomendacoes = [...new Set(entradas.map((e) => (e.recommendation ?? '').trim()).filter(Boolean))]

  const linhas: string[] = [
    `Corrigir na origem: ${regra.nome}.`,
    '',
    `Este tópico substitui ${contagem(results.length, 'tópico de conflito', 'tópicos de conflito')}: corrige-se no modelo, de uma vez, e não conflito a conflito. Na triagem, estes conflitos ficam ignorados pela regra "${regra.nome}".`,
  ]
  if (regra.motivo) linhas.push('', `Motivo: ${regra.motivo}`)
  linhas.push(`Condição da regra: ${textoDaCondicao(regra.condicao)}.`)
  if (recomendacoes.length === 1) linhas.push('', `Recomendação: ${recomendacoes[0]}`)
  else if (recomendacoes.length > 1) {
    linhas.push('', 'Recomendações:')
    for (const r of recomendacoes.slice(0, 5)) linhas.push(`- ${r}`)
  }
  // Os elementos, por especialidade (pela ordem ARQ, EST, MAC, HID, EEL).
  const ordem = ESPECIALIDADES.map((e) => e.sigla)
  const porEsp = new Map<string, ElementoDoModelador[]>()
  for (const el of elementos) {
    const k = el.especialidade ?? el.modelId
    const l = porEsp.get(k)
    if (l) l.push(el)
    else porEsp.set(k, [el])
  }
  const esps = [...porEsp.keys()].sort((a, b) => {
    const ia = ordem.indexOf(a)
    const ib = ordem.indexOf(b)
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b)
  })
  let listados = 0
  for (const esp of esps) {
    const els = porEsp.get(esp)!
    linhas.push('', `Elementos ${esp} (${els.length}):`)
    for (const el of els) {
      if (listados >= MAXIMO_DE_ELEMENTOS_NO_TOPICO) break
      const nome = el.nome ? ` ${el.nome}` : ''
      linhas.push(`- ${el.guid ?? `id ${el.localId}`} (${el.classe ?? 'sem classe'}${nome ? `,${nome}` : ''})`)
      listados += 1
    }
  }
  if (elementos.length > listados) {
    linhas.push('', `E mais ${elementos.length - listados} elementos (a lista completa está na triagem, regra "${regra.nome}").`)
  }

  const siglas = esps.filter((e) => ordem.includes(e))
  const labels = [...new Set(['Conflito', 'Corrigir na origem', ...siglas])]
  return {
    key: `regra:${regra.id}`,
    keys,
    identidade: `regra:${regra.id}`,
    title: `Corrigir na origem: ${regra.nome} (${contagem(elementos.length, 'elemento', 'elementos')}, ${contagem(results.length, 'interferência', 'interferências')})`,
    description: linhas.join('\n'),
    labels,
    type: TOPIC_TYPE_CLASH,
    status: 'Open',
    priority: prioridade ? PRIORITY_BCF[prioridade] : null,
    assignedTo: responsavel,
    notify,
    comentarios: [],
    dueDate: null,
    grupo: null,
  }
}
